import React, { useEffect, useMemo, useRef, useState } from "react";
import MaterialReactTable, {
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_RowSelectionState,
  MRT_SortingState,
} from "material-react-table";
import { Button } from "@fluentui/react-components";
import { FilterDismiss20Regular } from "@fluentui/react-icons";
import apiServices from "../../../service";
import OverlayLoader from "../../../components/OverlayLoader";

interface ClientRow {
  customer_id: string;
  first_names: string;
  last_name: string;
  email: string;
}

function ClientSelection(props: any) {
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  // Accumulates across pages/search/sort changes -- unlike the old approach
  // this is never reset on fetch, so a selection made on page 1 survives
  // paging to page 2 (and the banner replacement below can count it
  // directly). Rows are keyed by customer_id (getRowId), which is stable
  // across pages.
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  // Every client row ever loaded across pagination/search/sort changes, so a
  // row selected on an earlier page can still be resolved to a full
  // ClientRow after later pages replace `clients`.
  const seenClientsRef = useRef<Map<string, ClientRow>>(new Map());
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const columns = useMemo<MRT_ColumnDef<ClientRow>[]>(
    () => [
      {
        accessorKey: "first_names",
        header: "First Name",
      },
      {
        accessorKey: "last_name",
        header: "Last Name",
      },
      {
        accessorKey: "email",
        header: "Email",
        // Not one of the backend's sortable columns -- no clickable sort
        // header for it.
        enableSorting: false,
      },
    ],
    []
  );

  async function getClientList() {
    setLoading(true);
    try {
      const activeSort = sorting[0];
      const resp = await apiServices.client.getClientLists(
        pagination.pageIndex + 1,
        pagination.pageSize,
        searchText,
        undefined,
        undefined,
        undefined,
        undefined,
        activeSort?.id,
        activeSort ? (activeSort.desc ? "desc" : "asc") : undefined
      );

      setRowCount(resp?.data?.data?.count || 0);
      const transformedData = resp?.data?.data?.customerList.map(
        (client: any) => ({
          customer_id: client.customer_id,
          first_names: client?.first_names || "",
          last_name: client?.last_name || "",
          email: client?.email || "",
        })
      );

      setClients(transformedData);
      transformedData.forEach((client: ClientRow) =>
        seenClientsRef.current.set(client.customer_id, client)
      );
      props.setClientList(Array.from(seenClientsRef.current.values()));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getClientList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize, searchText, sorting]);

  // Derived straight from rowSelection + every row ever seen, rather than
  // merged incrementally into its own state -- the old merge-only-add
  // version could never shrink, so unchecking a row on an earlier page left
  // it "selected" forever. Using a ref here (not state) for seenClients is
  // safe because a row can only appear in rowSelection after getClientList
  // has already recorded it.
  const selectedClients = useMemo(
    () =>
      Array.from(seenClientsRef.current.values()).filter(
        (client) => rowSelection[client.customer_id]
      ),
    [rowSelection]
  );

  useEffect(() => {
    props.handleClientSelection?.(selectedClients);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClients]);

  return (
    <div>
      <OverlayLoader isLoading={loading} />
      <div className="table-in-popup">
        <MaterialReactTable<ClientRow>
          columns={columns}
          data={clients}
          onRowSelectionChange={setRowSelection}
          onPaginationChange={setPagination}
          onSortingChange={setSorting}
          rowCount={rowCount}
          manualPagination
          manualSorting
          enableMultiSort={false}
          state={{ pagination, rowSelection, sorting }}
          enableGlobalFilter
          enableColumnActions={false}
          // MRT's own alert banner only counts rows in the currently-loaded
          // page's row model, so under manualPagination it drops selections
          // made on earlier pages -- see Clients.tsx for the same fix.
          // rowSelection here now accumulates correctly across pages, so a
          // count read directly off it is accurate.
          positionToolbarAlertBanner="none"
          renderTopToolbarCustomActions={() => {
            const selectedCount = Object.values(rowSelection).filter(
              Boolean
            ).length;
            if (selectedCount === 0) return null;
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 4, paddingLeft: 8 }}>
                <span
                  style={{
                    fontSize: "0.875rem",
                    color: "rgba(0, 0, 0, 0.6)",
                  }}
                >
                  {selectedCount} of {rowCount} row(s) selected
                </span>
                <Button
                  appearance="subtle"
                  shape="circular"
                  style={{ minWidth: "auto" }}
                  icon={<FilterDismiss20Regular />}
                  aria-label="Clear selected rows"
                  title="Clear selected rows"
                  onClick={() => setRowSelection({})}
                />
              </div>
            );
          }}
          enableColumnFilters
          enableFullScreenToggle={false}
          enableRowSelection={(row) => !!row.original.email}
          enableMultiRowSelection
          getRowId={(row) => row.customer_id}
          muiTablePaginationProps={{
            showFirstButton: false,
            showLastButton: false,
          }}
          muiTableBodyCellProps={({ cell, row }) => {
            if (cell.column.id === "mrt-row-select") {
              return {
                sx: {
                  pointerEvents: row.original.email ? "auto" : "none",
                  opacity: row.original.email ? 1 : 0.5,
                },
                disabled: !row.original.email,
              };
            }
            return {
              style: {
                padding: "6px 12px",
                fontSize: "0.875rem",
              },
            };
          }}
          onGlobalFilterChange={setSearchText}
        />
      </div>
    </div>
  );
}

export default ClientSelection;
